/**
 * AuthProvider.js — Google OAuth via Google Identity Services (GIS).
 *
 * Uses the GIS "Sign In With Google" button / One Tap flow.
 * No extra npm dependencies required — the GIS script is loaded in index.html.
 *
 * Auth state shape:
 *   { userId, name, email, avatar, token }
 *
 * The token is a 90-day server-side session token (NOT a raw Google token).
 * Google tokens are only sent to /api/auth/login once; all subsequent
 * API calls use the session token.
 *
 * Persisted to localStorage so auth survives refreshes.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { trackEvent } from "../analytics";

const AuthContext = createContext(null);

const STORAGE_KEY = "fantasy_arena_auth_v4";
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const DEV_MODE = process.env.NODE_ENV === "development" && !CLIENT_ID;

/**
 * Decode a JWT payload without a library (GIS returns a credential JWT).
 */
function decodeJwtPayload(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function loadPersistedUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persistUser(user) {
  if (user) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

/**
 * Save user profile via server-side API (authenticated).
 */
async function saveProfileViaApi(token, avatar) {
  try {
    await fetch('/api/save-profile', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ avatar }),
    });
  } catch {
    // Non-critical, don't block auth flow
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // Clean up old storage keys — users will re-authenticate once
    localStorage.removeItem("fantasy_arena_auth_v2");
    localStorage.removeItem("fantasy_arena_auth_v3");

    const persisted = loadPersistedUser();
    if (persisted && !persisted.token) {
      return null;
    }
    return persisted;
  });
  const tokenRef = useRef(user?.token || null);

  // On initial load, verify stored token with server (once per session)
  const verifiedRef = useRef(false);
  useEffect(() => {
    if (verifiedRef.current) return;
    const persisted = loadPersistedUser();
    if (!persisted?.token) return;

    verifiedRef.current = true;

    // Verify the token is still valid
    fetch('/api/verify-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${persisted.token}`,
      },
    })
      .then(r => {
        if (!r.ok) {
          // Token expired or invalid — clear auth state
          console.warn('[Auth] Stored token is invalid, clearing auth state');
          verifiedRef.current = false;
          setUser(null);
          persistUser(null);
          tokenRef.current = null;
        } else {
          // Token valid — save profile (only on first load)
          saveProfileViaApi(persisted.token, persisted.avatar);
        }
      })
      .catch(() => {
        // Network error — don't force logout, keep local state
        verifiedRef.current = false;
      });
  }, []);

  /**
   * Exchange a Google token for a 90-day server-side session.
   * Called by both GIS One Tap and OAuth2 popup flows.
   */
  const loginWithServer = useCallback(async (googleToken, googleUser) => {
    try {
      const r = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ googleToken }),
      });

      if (!r.ok) {
        console.error('[Auth] Server login failed:', r.status);
        return;
      }

      const data = await r.json();

      const authUser = {
        userId: data.user.userId,
        name: data.user.name || googleUser.name || "",
        email: data.user.email || googleUser.email || "",
        avatar: googleUser.avatar || "",
        token: data.token, // 90-day session token
      };

      setUser(authUser);
      persistUser(authUser);
      tokenRef.current = data.token;
      trackEvent("sign_in", { userId: authUser.userId });
      saveProfileViaApi(data.token, authUser.avatar);
    } catch (err) {
      console.error('[Auth] Server login error:', err);
    }
  }, []);

  // Handle the credential response from Google (GIS auto-select / One Tap)
  const handleCredentialResponse = useCallback((response) => {
    const payload = decodeJwtPayload(response.credential);
    if (!payload) return;

    const googleUser = {
      name: payload.name || "",
      email: payload.email || "",
      avatar: payload.picture || "",
    };

    loginWithServer(response.credential, googleUser);
  }, [loginWithServer]);

  // Initialize GIS once the script is loaded
  useEffect(() => {
    if (!CLIENT_ID) return;

    function init() {
      if (!window.google?.accounts?.id) return;

      window.google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: true,
      });
    }

    if (window.google?.accounts?.id) {
      init();
    } else {
      const interval = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(interval);
          init();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [handleCredentialResponse]);

  const signIn = useCallback(() => {
    // Dev mode: skip OAuth and use a fake admin user
    if (DEV_MODE) {
      const devEmail = window.__FA_ADMIN_EMAIL__ || process.env.REACT_APP_ADMIN_EMAIL || "admin@dev.local";
      const devUser = {
        userId: "dev_user_001",
        name: "Dev Admin",
        email: devEmail,
        avatar: "",
        token: "dev_token", // Placeholder token for dev mode
      };
      console.log("[Auth] DEV MODE: Logging in as", devEmail);
      setUser(devUser);
      persistUser(devUser);
      tokenRef.current = "dev_token";
      trackEvent("sign_in", { userId: devUser.userId, dev: true });
      return;
    }

    if (!CLIENT_ID) {
      console.warn("Google Client ID not configured. Set REACT_APP_GOOGLE_CLIENT_ID in .env");
      return;
    }

    // Use the OAuth2 token client — opens a real popup
    if (window.google?.accounts?.oauth2) {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: "openid profile email",
        callback: (tokenResponse) => {
          if (tokenResponse.access_token) {
            const accessToken = tokenResponse.access_token;

            // Fetch Google user info, then exchange for server session
            fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
              headers: { Authorization: `Bearer ${accessToken}` },
            })
              .then((r) => r.json())
              .then((info) => {
                const googleUser = {
                  name: info.name || "",
                  email: info.email || "",
                  avatar: info.picture || "",
                };
                loginWithServer(accessToken, googleUser);
              });
          }
        },
      });
      client.requestAccessToken();
    }
  }, [loginWithServer]);

  const signOut = useCallback(() => {
    setUser(null);
    persistUser(null);
    tokenRef.current = null;
    trackEvent("sign_out");
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  }, []);

  /**
   * Get the current auth token for API calls.
   * Returns the server-side session token.
   */
  const getToken = useCallback(() => {
    return tokenRef.current || user?.token || null;
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, signIn, signOut, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth state.
 * Returns { user, signIn, signOut, getToken }.
 * user is null when not signed in.
 * getToken() returns the server-side session token for API calls.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
